import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@core/services/api/apiRequest";
import type { InvitationTemplate, InvitationType } from "../invitation.types";

export type UseFetchInvitationTemplatesArgs = {
  /** Program ID to fetch templates for */
  programId: number;
  /** Optional invitation type filter (participant, orgAdmin, orgAdminPrimary, participantFriend) */
  type?: InvitationType;
  /** Whether the query is enabled */
  enabled?: boolean;
};

/**
 * Hook to fetch invitation templates for a program.
 *
 * Templates are reusable invitation blueprints (is_template=True) that can be
 * instantiated for organizations or users.
 *
 * Results are scoped by user role:
 * - Org admins see all template types
 * - Participants see only participantFriend templates
 */
export function useFetchInvitationTemplates({
  programId,
  type,
  enabled = true,
}: UseFetchInvitationTemplatesArgs) {
  return useQuery<InvitationTemplate[]>({
    queryKey: ["invitation-templates", programId, type],
    queryFn: async () => {
      const params = type ? `?type=${type}` : "";
      return apiRequest(
        `/program/${programId}/invitation-templates/${params}`,
        "GET"
      );
    },
    enabled: enabled && !!programId,
    staleTime: 5 * 60 * 1000, // 5 minutes - templates don't change often
  });
}
